import React, { useContext, useEffect, useRef, useState } from 'react'
import { Alert, Button, CustomInput, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import ApiService from '../../../data/services/ApiService'
import { GeneralContext } from '../../../context'
import PricingShippingPreview, { PricingIssues } from './PricingShippingPreview'

// Read bytes once. Both requests send this same immutable File snapshot, without
// decoding/re-encoding CSV (including BOM, delimiter and line-ending bytes).
const snapshotFile = file => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(new File([reader.result], file.name, { type: file.type, lastModified: file.lastModified }))
    reader.onerror = () => reject(new Error('No pude leer el archivo. Selecciónalo nuevamente.'))
    reader.onabort = () => reject(new Error('La lectura del archivo se canceló.'))
    reader.readAsArrayBuffer(file)
})
const multipart = (file, identifier, token) => {
    const data = new FormData()
    data.append('file', file)
    data.append('identifier', identifier)
    if (token) data.append('previewToken', token)
    return data
}
const displayResult = result => result ? { totals: result.totals, errors: result.errors, warnings: result.warnings, preview: result.preview } : null
const canReview = (response, file) => {
    const result = response?.data?.result
    return Boolean(response?.data?.success === true && result?.preview?.version === 1 && result.preview.importable === true &&
        result.preview.plan && Array.isArray(result.preview.plan.records) && Array.isArray(result.preview.plan.sharedPriceEffects) &&
        !(result.errors?.length) && !(result.totals?.errors > 0) && typeof result.previewToken === 'string' && result.previewToken.trim() &&
        Number.isFinite(Date.parse(result.previewExpiresAt)) && Date.parse(result.previewExpiresAt) > Date.now() && file)
}

export const commitOutcome = response => {
    const data = response?.data
    if (data?.committed === true && data.outcome === 'committed_with_status_error') return { color: 'warning', message: 'Cambios guardados, pero no se pudo registrar el estado del proceso. Concilia el estado; no repitas la importación.' }
    if (data?.committed === true && data.outcome === 'committed' && data.success === true) return { color: 'success', message: 'Cambios guardados correctamente.' }
    if (data?.outcome === 'commit_unknown' || data?.code === 'E_PRICING_COMMIT_UNKNOWN') return { color: 'danger', uncertain: true, message: 'La API no pudo confirmar el resultado de la transacción. Comprueba la configuración real antes de volver a validar o importar. No repitas la importación a ciegas.' }
    if (response?.status === 409 || data?.outcome === 'preview_conflict') return { color: 'warning', message: 'La vista previa ya no es válida. Valida nuevamente el archivo y revisa el nuevo plan antes de confirmar. No se aplicaron cambios.' }
    if (data?.committed === false) {
        const messages = {
            validation_failed: 'La validación falló. No se aplicaron cambios. Corrige el archivo y valida nuevamente.',
            transaction_failed: 'La transacción falló y no se aplicaron cambios. Investiga la causa y valida nuevamente.',
            configuration_error: 'No se aplicaron cambios. Un administrador debe corregir la configuración de firma o los índices requeridos antes de validar nuevamente.',
            status_unavailable: 'No se pudo iniciar el proceso. La transacción de configuración no comenzó. Valida nuevamente cuando se restablezca el servicio.'
        }
        if (messages[data.outcome]) return { color: 'danger', message: messages[data.outcome] }
    }
    return { color: 'danger', uncertain: true, message: 'Respuesta perdida, incompleta o tiempo de espera agotado: no sabemos si se guardaron los cambios. Comprueba la configuración real antes de volver a validar o importar. No se reintentará automáticamente.' }
}

const PricingShippingImport = ({ onBusyChange, selectedMaterial }) => {
    const { setLoading } = useContext(GeneralContext)
    const [file, setFile] = useState(null)
    const [result, setResult] = useState(null)
    const [proof, setProof] = useState(null)
    const [busy, setBusy] = useState('')
    const [notice, setNotice] = useState('')
    const [outcome, setOutcome] = useState(null)
    const [reconciled, setReconciled] = useState(false)
    const [confirming, setConfirming] = useState(false)
    const [confirmed, setConfirmed] = useState(false)
    const generation = useRef(0)
    const applying = useRef(false)
    const mounted = useRef(true)
    const input = useRef(null)
    useEffect(() => () => { mounted.current = false; generation.current += 1 }, [])
    useEffect(() => {
        onBusyChange(Boolean(busy))
        setLoading(Boolean(busy))
        return () => { onBusyChange(false); setLoading(false) }
    }, [busy, onBusyChange, setLoading])
    useEffect(() => {
        if (!proof) return
        const expire = () => {
            setProof(null); setConfirming(false); setConfirmed(false)
            setNotice('La validación venció. Valida nuevamente el archivo y revisa el nuevo plan.')
        }
        const timer = setTimeout(expire, Math.max(0, Math.min(proof.expires - Date.now(), 2147483647)))
        return () => clearTimeout(timer)
    }, [proof])
    useEffect(() => {
        if (busy !== 'applying') return
        const warn = event => { event.preventDefault(); event.returnValue = '' }
        window.addEventListener('beforeunload', warn)
        return () => window.removeEventListener('beforeunload', warn)
    }, [busy])
    const invalidate = () => { setProof(null); setConfirming(false); setConfirmed(false) }
    const unresolved = outcome?.uncertain && !reconciled
    const ready = Boolean(proof && proof.file === file && proof.expires > Date.now() && !busy && !unresolved)
    const selectFile = async event => {
        if (applying.current) return
        const revision = ++generation.current
        const selected = event.target.files?.[0]
        invalidate(); setFile(null); setResult(null); setNotice('')
        if (!selected) { setBusy(''); return }
        if (!/\.csv$/i.test(selected.name)) { setBusy(''); setNotice('Selecciona un archivo CSV (.csv).'); if (input.current) input.current.value = ''; return }
        setBusy('reading')
        try {
            const snapshot = await snapshotFile(selected)
            if (mounted.current && revision === generation.current) setFile(snapshot)
        } catch (error) {
            if (mounted.current && revision === generation.current) setNotice(error.message)
        } finally {
            if (mounted.current && revision === generation.current) setBusy('')
        }
    }
    const validate = async () => {
        if (!file || busy || unresolved || applying.current) return
        const revision = ++generation.current
        invalidate(); setResult(null); setNotice(''); setOutcome(null); setReconciled(false); setBusy('validating')
        try {
            const response = await ApiService.validatePricingShippingBulk(multipart(file, `CE-${Date.now().toString(36)}`))
            if (!mounted.current || revision !== generation.current) return
            setResult(displayResult(response.data?.result))
            if (canReview(response, file)) {
                setProof({ file, token: response.data.result.previewToken, expires: Date.parse(response.data.result.previewExpiresAt) })
            } else setNotice('No hay una validación vigente con un plan importable. Revisa los errores y valida nuevamente.')
        } catch (error) {
            if (!mounted.current || revision !== generation.current) return
            setResult(displayResult(error.response?.data?.result))
            setNotice(error.response?.data?.message || 'No pude validar el archivo. No se han solicitado cambios.')
        } finally {
            if (mounted.current && revision === generation.current) setBusy('')
        }
    }
    const openConfirmation = () => {
        if (!ready || proof.expires <= Date.now()) { invalidate(); setNotice('Valida nuevamente el archivo antes de aplicar.'); return }
        setConfirmed(false); setConfirming(true)
    }
    const apply = async () => {
        if (applying.current || !confirming || !confirmed) return
        if (!ready || proof.expires <= Date.now() || proof.file !== file) { invalidate(); setNotice('La validación venció. Valida y revisa nuevamente.'); return }
        const reviewed = proof
        // Consume the proof before sending; every attempt requires a fresh review.
        applying.current = true
        invalidate(); setBusy('applying'); setNotice(''); setOutcome(null)
        const identifier = `CE-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
        let response
        try {
            response = await ApiService.importPricingShippingBulk(multipart(reviewed.file, identifier, reviewed.token))
        } catch (error) { response = error.response }
        finally {
            applying.current = false
            if (mounted.current) {
                setOutcome({ ...commitOutcome(response), filename: reviewed.file.name, identifier: response?.data?.identifier || identifier, code: response?.data?.code, errors: response?.data?.result?.errors || [] })
                setReconciled(false); setBusy('')
            }
        }
    }
    return <React.Fragment>
        <FormGroup><Label for="pricing-csv-file">Archivo CSV</Label><Input innerRef={input} id="pricing-csv-file" type="file" accept=".csv,text/csv" disabled={busy === 'applying'} onChange={selectFile} /></FormGroup>
        {file && <p>Archivo seleccionado: <strong>{file.name}</strong></p>}
        <Button color="info" disabled={!file || Boolean(busy) || unresolved} onClick={validate} className="mr-2">Validar</Button>
        <Button color="primary" disabled={!ready} onClick={openConfirmation}>Aplicar cambios</Button>
        {busy && <p role="status" className="mt-2">{busy === 'reading' ? 'Leyendo archivo…' : busy === 'validating' ? 'Validando archivo…' : 'Aplicando cambios. Mantén abierta esta página; no repitas la solicitud.'}</p>}
        {proof && <p className="mt-2">Validación vigente hasta {new Date(proof.expires).toLocaleTimeString('es-MX')}. Revisa la vista previa antes de aplicar.</p>}
        {notice && <Alert color="warning" className="mt-3" role="alert">{notice}</Alert>}
        {outcome && <Alert color={outcome.color} className="mt-3" role="alert">
            <p>{outcome.message}</p><p>Archivo de la solicitud: <strong>{outcome.filename}</strong></p><p>Referencia de la solicitud: <strong>{outcome.identifier}</strong>{outcome.code && <span> · {outcome.code}</span>}</p>
            <PricingIssues issues={outcome.errors} title="Errores de la importación" />
            {outcome.uncertain && <CustomInput id="pricing-reconciled" type="checkbox" label="He comprobado la configuración real y conciliado el resultado antes de iniciar una nueva validación." checked={reconciled} onChange={event => setReconciled(event.target.checked)} />}
        </Alert>}
        <PricingShippingPreview result={result} />
        {/* Bootstrap 3's global .fade stays transparent with Bootstrap 4's .show. */}
        <Modal isOpen={confirming} fade={false} toggle={() => setConfirming(false)} labelledBy="pricing-confirm-title" size="lg">
            <ModalHeader id="pricing-confirm-title" toggle={() => setConfirming(false)}>Confirmar cambios de precios y envíos</ModalHeader>
            <ModalBody>
                <p>Archivo: <strong>{file?.name}</strong></p>
                {selectedMaterial && <p>Material de referencia: <strong>{selectedMaterial}</strong></p>}
                <p>Se aplicará el plan completo revisado, incluidas las filas de otros materiales, las activaciones/desactivaciones y los efectos de precios compartidos de María Luisa.</p>
                <p>Una respuesta perdida puede requerir comprobar la configuración real. No repitas una importación con resultado incierto.</p>
                <CustomInput id="pricing-confirm-reviewed" type="checkbox" label="He revisado todas las filas, los valores antes/después y las advertencias, y confirmo aplicar este plan." checked={confirmed} onChange={event => setConfirmed(event.target.checked)} />
            </ModalBody>
            <ModalFooter><Button color="secondary" onClick={() => setConfirming(false)}>Cancelar</Button><Button color="primary" disabled={!ready || !confirmed} onClick={apply}>Confirmar y aplicar</Button></ModalFooter>
        </Modal>
    </React.Fragment>
}
export default PricingShippingImport
